import {
  ArrowLeftIcon,
  CheckCircledIcon,
  Cross1Icon,
  CrossCircledIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import {
  ArrowRightIcon,
  Edit2Icon,
  RefreshCwIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { FeedAccessErrorPage } from "@/components/FeedAccessErrorPage";
import { useToast } from "@/components/hooks/use-toast";
import { MonetizationSection } from "@/components/MonetizationSection";
import { Page } from "@/components/Page";
import { PatreonConnectionNotice } from "@/components/PatreonConnectionNotice";
import { PostListDialog } from "@/components/PostListDialog";
import { PreviewPost } from "@/components/PreviewFeed";
import { TagListItem } from "@/components/TagListItem";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleSelect,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Anchor, H2, Link } from "@/components/ui/typography";
import { ViewFeedHeader } from "@/components/ViewFeedHeader";
import {
  addSubscriberListToFeed,
  rebuildFeed,
  clearFeed,
  getBackfillStatus,
  editFeed,
  feedQueryOptions,
  getSubscriberListsQueryOptions,
  removeSubscriberListFromFeed,
  setFeedPublic,
  setContrailsForAlgorithm,
  setAlgoSetting,
  AppSetting,
  getCurrentUserQueryOptions,
  getCocoreStatusQueryOptions,
  getFeedPulseStatusQueryOptions,
  requestFeedPulseNow,
  getPatreonAccountQueryOptions,
  linkPatreon,
  getPatreonCampaignsQueryOptions,
  resyncPatreonCampaigns,
  getPatreonCampaginTeirsQueryOptions,
  getPatreonCapabilitiesQueryOptions,
  addCapabilityToFeed,
  getCapabilitiesForFeedQueryOptions,
  PatreonCapability,
  deleteCapabilityFromFeed,
  AddCapabilityToFeedInput,
} from "@/lib/api";
import { runBackfill } from "@/lib/feedWrites";
import { Feed, isSimpleEditorSnapshot } from "@/lib/types";
import { useFeedAccess } from "@/lib/useFeedAccess";
import { atUriToBskyUrl, cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/app/_sidebar-layout/feed/$feedId/settings",
)({
  component: RouteComponent,
  // A revoked collaborator gets 403 here; say so instead of bouncing them to /login.
  errorComponent: FeedAccessErrorPage,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    const feed = await queryClient.ensureQueryData(
      feedQueryOptions(parseInt(feedId)),
    );

    await Promise.all([
      queryClient.prefetchQuery(getSubscriberListsQueryOptions()),
      queryClient.prefetchQuery(getCurrentUserQueryOptions()),
      queryClient.prefetchQuery(getPatreonAccountQueryOptions()),
    ]);

    return {
      title: feed.display_name,
    };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.title }],
  }),
});

function AddSubscriberListButton({ feed }: { feed: Feed }) {
  const { toast } = useToast();
  const { data: lists } = useSuspenseQuery(getSubscriberListsQueryOptions());
  const [open, setOpen] = useState(false);
  const onOpenChange = useCallback((open: boolean) => {
    setOpen(open);
  }, []);
  const addSubscriberListMutation = useMutation({
    mutationFn: async (listId: number) => {
      await addSubscriberListToFeed(feed.id, listId);
      onOpenChange(false);
    },
    onSuccess: () =>
      toast({
        title: "Feed subscriber list updated",
        description: "Your feed subscriber list has been updated.",
      }),
    onError: (error) =>
      toast({
        title: "Error updating feed subscriber list",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <SimpleTooltip message="Add a subscriber list" asChild>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="smIcon">
            <PlusIcon />
            <span className="sr-only">Add list</span>
          </Button>
        </PopoverTrigger>
      </SimpleTooltip>
      <PopoverContent className="p-0">
        <Command>
          <CommandInput placeholder="Search lists" />
          <CommandList>
            <CommandGroup>
              {lists.map((list) => (
                <CommandItem
                  key={`${list.id}`}
                  onSelect={() => addSubscriberListMutation.mutate(list.id)}
                >
                  {list.list_type === "memberships" ? (
                    <CheckCircledIcon className="text-green-10 dark:text-greendark-10" />
                  ) : (
                    <CrossCircledIcon className="text-red-10 dark:text-reddark-10" />
                  )}
                  {list.display_name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function AddPatreonTierDialogContent({
  setOpen,
  feedId,
}: {
  setOpen: (open: boolean) => void;
  feedId: number;
}) {
  const { data: feed } = useSuspenseQuery(feedQueryOptions(feedId));

  const [userType, setUserType] = useState<"anon" | "user" | "member">("anon");
  const { data: campaigns = [] } = useQuery(getPatreonCampaignsQueryOptions());
  const selectedCampaign = campaigns[0];
  const { data: tiers } = useQuery(
    getPatreonCampaginTeirsQueryOptions(selectedCampaign?.id.toString()),
  );
  const { data: capabilities } = useQuery(getPatreonCapabilitiesQueryOptions());

  const [selectedTier, setSelectedTier] = useState<string | undefined>(
    undefined,
  );

  const [selectedCapabilities, setSelectedCapabilities] = useState<string[]>(
    [],
  );

  const { toast } = useToast();
  const addCapabilityMutation = useMutation({
    onSuccess: () => setOpen(false),
    onError: (error) =>
      toast({
        title: "Error adding capability",
        description: error.message,
        variant: "destructive",
      }),
    mutationFn: async () => {
      const capabilities = selectedCapabilities.map((v) => parseInt(v));

      if (userType === "member") {
        if (!selectedTier) {
          throw new Error("No tier selected");
        }

        await addCapabilityToFeed({
          membership_type: "member",
          algo_id: feed.id,
          capability_ids: capabilities,
          tier_id: parseInt(selectedTier),
        });
      } else {
        await addCapabilityToFeed({
          membership_type: userType,
          algo_id: feed.id,
          capability_ids: capabilities,
        });
      }
    },
  });

  useEffect(() => {
    resyncPatreonCampaigns().catch(console.error);
  }, []);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add a Capability</DialogTitle>
        <DialogDescription>
          Configure the capabilities that are available for certain user types.
        </DialogDescription>
      </DialogHeader>

      <Separator />

      <div className="flex flex-col gap-6 py-4">
        <div className="flex flex-col gap-2">
          <Label>User Type</Label>
          <Select
            value={userType}
            onValueChange={(value) =>
              setUserType(value as "anon" | "user" | "member")
            }
          >
            <SelectTrigger size="sm">
              <SelectValue placeholder="Select a user type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="anon">Anonymous User</SelectItem>
              <SelectItem value="user">Authenticated User</SelectItem>
              <SelectItem value="member">Patreon Member</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {userType === "member" && (
          <>
            {selectedCampaign && tiers && (
              <div className="flex flex-col gap-2">
                <Label>Tier</Label>
                <Select value={selectedTier} onValueChange={setSelectedTier}>
                  <SelectTrigger size="sm">
                    <SelectValue placeholder="Select a Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiers.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}

        {capabilities && (userType !== "member" || selectedTier) && (
          <div className="flex flex-col items-start gap-2">
            <Label>Capabilities</Label>

            {selectedCapabilities.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedCapabilities.map((capabilityId) => {
                  const capability = capabilities.find(
                    (c) => c.id === parseInt(capabilityId),
                  );

                  if (!capability) {
                    return null;
                  }

                  return (
                    <CapabilityListItem
                      key={capability.id}
                      capability={capability}
                      onDelete={() => {
                        setSelectedCapabilities(
                          selectedCapabilities.filter(
                            (c) => c !== `${capability.id}`,
                          ),
                        );
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-slate-dim py-1">
                No capabilities selected
              </div>
            )}

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  Add capability
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0">
                <Command>
                  <CommandInput placeholder="Search lists" />
                  <CommandList>
                    <CommandGroup>
                      {capabilities.map((capability) => (
                        <CommandItem
                          key={`${capability.id}`}
                          onSelect={() => {
                            if (
                              selectedCapabilities.includes(`${capability.id}`)
                            ) {
                              setSelectedCapabilities(
                                selectedCapabilities.filter(
                                  (c) => c !== `${capability.id}`,
                                ),
                              );
                            } else {
                              setSelectedCapabilities([
                                ...selectedCapabilities,
                                `${capability.id}`,
                              ]);
                            }
                          }}
                        >
                          {selectedCapabilities.includes(`${capability.id}`) ? (
                            <CheckCircledIcon />
                          ) : (
                            <div className="opacity-0">
                              <CheckCircledIcon />
                            </div>
                          )}
                          {capability.display_name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          disabled={
            selectedCapabilities.length === 0 || addCapabilityMutation.isPending
          }
          onClick={() => addCapabilityMutation.mutate()}
        >
          {addCapabilityMutation.isPending ? "Adding..." : "Add"}
        </Button>
      </DialogFooter>
    </>
  );
}

function CapabilityListItem({
  capability,
  onDelete,
}: {
  capability: PatreonCapability;
  onDelete: () => void;
}) {
  const item = (
    <TagListItem
      key={capability.id}
      tag={{
        id: capability.id,
        display_name: capability.display_name,
        color: capability.display_name.toLowerCase().includes("cannot")
          ? "red"
          : "green",
      }}
      onDelete={onDelete}
    />
  );

  const tooltipMessage =
    capability.capability === "sponsor_free"
      ? "No ads will be shown for these users."
      : capability.capability === "cant_see"
      ? "These users will not be able to see your feed's posts."
      : undefined;

  if (tooltipMessage) {
    return <SimpleTooltip message={tooltipMessage}>{item}</SimpleTooltip>;
  }

  return item;
}

function ActiveCapabilityListItem({
  capability,
  algoId,
  membershipType,
  tierId,
}: {
  capability: PatreonCapability;
  algoId: number;
  membershipType: "anon" | "user" | "member";
  tierId?: number;
}) {
  const deleteCapabilityMutation = useMutation({
    mutationFn: async () => {
      await deleteCapabilityFromFeed({
        algo_id: algoId,
        membership_type: membershipType,
        capability_ids: [capability.id],
        tier_id: tierId,
      } as AddCapabilityToFeedInput);
    },
  });

  return (
    <CapabilityListItem
      capability={capability}
      onDelete={() => deleteCapabilityMutation.mutate()}
    />
  );
}

function PatreonCampaignPicker({ feedId }: { feedId: number }) {
  const [addPatreonTierDialogOpen, setAddPatreonTierDialogOpen] =
    useState(false);

  return (
    <div className="flex flex-col items-end gap-2 max-w-[60ch]">
      <div className="flex items-center gap-2">
        <Dialog
          open={addPatreonTierDialogOpen}
          onOpenChange={setAddPatreonTierDialogOpen}
        >
          <SimpleTooltip message="Add a subscriber list" asChild>
            <DialogTrigger asChild>
              <Button variant="ghost" size="smIcon">
                <PlusIcon />
                <span className="sr-only">Add Tier</span>
              </Button>
            </DialogTrigger>
          </SimpleTooltip>
          <DialogContent>
            <AddPatreonTierDialogContent
              setOpen={setAddPatreonTierDialogOpen}
              feedId={feedId}
            />
          </DialogContent>
        </Dialog>
        <SimpleTooltip message="Resync Patreon Data">
          <Button
            variant="ghost"
            size="smIcon"
            onClick={() => {
              resyncPatreonCampaigns().catch(console.error);
            }}
          >
            <span className="sr-only">Resync Patreon data</span>
            <RefreshCwIcon />
          </Button>
        </SimpleTooltip>
      </div>
    </div>
  );
}

function PatreonCapabilityList({ feedId }: { feedId: number }) {
  const { data: activeCapabilityEntry } = useQuery(
    getCapabilitiesForFeedQueryOptions(feedId),
  );
  const { data: capabilities } = useQuery(getPatreonCapabilitiesQueryOptions());
  const groupNames = useMemo(() => {
    return {
      anon: "Anonymous",
      user: "User",
      member: "Member",
    } as Record<string | number, string>;
  }, []);
  const capabilityMap = useMemo(() => {
    const value: Record<string | number, PatreonCapability[]> = {
      anon: [],
      user: [],
    };

    activeCapabilityEntry?.forEach((capabilityEntry) => {
      const capability = capabilities?.find(
        (c) => c.id === capabilityEntry.reader_capability_id,
      );

      if (!capability) {
        return;
      }

      if (capabilityEntry.membership_type === "member") {
        if (capabilityEntry.tier_id) {
          if (!value[capabilityEntry.tier_id]) {
            value[capabilityEntry.tier_id] = [];
          }

          value[capabilityEntry.tier_id]?.push(capability);
          // eslint-disable-next-line react-hooks/immutability
          groupNames[capabilityEntry.tier_id] = capabilityEntry.tier
            .patreon_campaign.rss_feed_title
            ? `${capabilityEntry.tier.title} (${capabilityEntry.tier.patreon_campaign.rss_feed_title})`
            : capabilityEntry.tier.title;
        }
      } else {
        value[capabilityEntry.membership_type]?.push(capability);
      }
    });

    return value;
  }, [activeCapabilityEntry, capabilities, groupNames]);

  return (
    <div className="flex gap-6">
      {Object.entries(capabilityMap).map(([type, entries]) => {
        if (!entries.length) {
          return null;
        }

        return (
          <div className="flex flex-col gap-2" key={type}>
            <div className="text-sm text-slate-dim font-semibold">
              {groupNames[type] || type}
            </div>

            {entries.map((capability) => {
              const tierId =
                type !== "anon" && type !== "user"
                  ? activeCapabilityEntry?.find(
                      (c) => c.reader_capability_id === capability.id,
                    )?.tier_id
                  : null;

              return (
                <ActiveCapabilityListItem
                  key={capability.id}
                  capability={capability}
                  algoId={feedId}
                  tierId={tierId || undefined}
                  membershipType={
                    type === "anon"
                      ? "anon"
                      : type === "user"
                      ? "user"
                      : "member"
                  }
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function PatreonSettings({ feedId }: { feedId: number }) {
  const { data: patreonAccount } = useQuery(getPatreonAccountQueryOptions());
  const { data: activeCapabilityEntry } = useQuery(
    getCapabilitiesForFeedQueryOptions(feedId),
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Shown here too, not just on the account page: this is where a creator
          wires a campaign to a feed, so it is where they notice the tiers have
          gone stale. */}
      <PatreonConnectionNotice
        state={patreonAccount?.account?.connection_state ?? null}
        lastSyncAt={patreonAccount?.account?.last_sync_at}
      />
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex flex-col gap-1 max-w-[60ch]">
            <label
              htmlFor="monetization-mode"
              className="md:text-lg font-medium"
            >
              Patreon
            </label>
            <div className="text-sm text-slate-dim">
              Grant privileges to your Patreon followers.
            </div>

            {activeCapabilityEntry && activeCapabilityEntry.length > 0 && (
              <div className="text-sm text-slate-dim">
                Make sure to share{" "}
                <Anchor
                  href={`https://graze.social/app/connect/patreon`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "inherit" }}
                >
                  this link
                </Anchor>{" "}
                with your followers so they can get their privileges.
              </div>
            )}
          </div>
        </div>

        {patreonAccount ? (
          <div className="flex items-center gap-2">
            <PatreonCampaignPicker feedId={feedId} />
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => {
              linkPatreon().catch(console.error);
            }}
          >
            Link Patreon
          </Button>
        )}
      </div>
      <PatreonCapabilityList feedId={feedId} />
    </div>
  );
}

function useHasMemberOnlySetting(feedId: number) {
  const { data: activeCapabilityEntry } = useQuery(
    getCapabilitiesForFeedQueryOptions(feedId),
  );
  // 3 is "cannot see feed"
  return activeCapabilityEntry?.some((c) => c.reader_capability_id === 3);
}

function PatreonWarning({ feedId }: { feedId: number }) {
  const hasMemberOnlySetting = useHasMemberOnlySetting(feedId);

  if (!hasMemberOnlySetting) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <SimpleTooltip message="This feed is a Patreon-only feed. Only users with Patreon membership will be able to view this feed.">
        <div className="rounded-lg bg-yellow-action p-2">
          <TriangleAlertIcon className="w-3 h-3 text-yellow-dim" />
        </div>
      </SimpleTooltip>
    </div>
  );
}

function SwitchToFullEditorSetting({ feedId }: { feedId: number }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const { data: feed } = useSuspenseQuery(feedQueryOptions(feedId));
  const onSubmit = useMutation({
    onSuccess: () => {
      toast({
        title: "Feed updated",
        description: "Your feed now uses the full editor.",
      });
      setOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error updating feed",
        description: error.message,
        variant: "destructive",
      });
    },
    mutationFn: () => {
      const form = new FormData();

      form.set(
        "algorithm_manifest",
        JSON.stringify(feed.draft_algorithm_manifest.manifest),
      );
      form.set("metadata", JSON.stringify({}));

      return editFeed(feedId, form);
    },
  });

  return (
    <div className="flex items-center gap-4">
      <div className="flex-1">
        <div className="flex flex-col gap-1 max-w-[60ch]">
          <div className="md:text-lg font-medium">Full Editor Mode</div>
          <div className="text-sm text-slate-dim">
            You are currently using the simplified version of Graze. If you want
            to use the full power of graze to create your feed, you can switch
            to the full editor.
          </div>
        </div>
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="destructive">Switch to full editor</Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to full editor</AlertDialogTitle>
            <AlertDialogDescription>
              If you switch to the full editor, you CANNOT switch back to the
              simplified editor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={onSubmit.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              isLoading={onSubmit.isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit.mutate();
              }}
            >
              Switch to full editor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Feed Pulse opt-in. The switch is disabled until the owner has connected
 * co/core on the account page, because the daily job runs on their account.
 */
function FeedPulseSettings({
  feed,
  onToggle,
}: {
  feed: Feed;
  onToggle: (enabled: boolean) => void;
}) {
  const { data: cocore } = useQuery(getCocoreStatusQueryOptions());
  const { data: pulseStatus } = useQuery(
    getFeedPulseStatusQueryOptions(feed.id),
  );
  const connected = Boolean(cocore?.connected);
  const lastJob = pulseStatus?.last_job;
  const latest = pulseStatus?.latest_pulse;
  const inFlight = lastJob?.state === "queued" || lastJob?.state === "running";
  const { toast } = useToast();
  const pulseNowMutation = useMutation({
    mutationFn: () => requestFeedPulseNow(feed.id),
    onError: (error) =>
      toast({
        title: "Could not start a pulse",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <div className="flex flex-col gap-4">
      <H2 className="mt-2">Feed Pulse</H2>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="flex flex-col gap-1 max-w-[60ch]">
            <div className="md:text-lg font-medium">Daily Feed Pulse</div>
            <div className="text-sm text-slate-dim">
              Each morning, a short summary of yesterday&apos;s most-viewed posts
              appears on your feed page{feed.public ? " and at the top of the public page" : ""}:
              a headline, a few sentences, and the themes. It is written on a co/core
              member&apos;s Mac using your own atmosphere account (about 2,000
              credits a day), and every pulse links to its signed receipt.
              {!feed.public && " This feed is private, so the pulse shows only to you on the feed page."}
            </div>
            {!connected && !cocore?.needs_reauth && !cocore?.out_of_credits && (
              <div className="text-sm text-slate-dim">
                <ButtonLink to="/app/account" variant="link" size="sm" className="px-0">
                  Connect co/core in your account settings
                </ButtonLink>{" "}
                to turn this on.
              </div>
            )}
            {cocore?.needs_reauth && (
              <div className="text-sm text-slate-dim">
                Your co/core connection needs a sign-in;{" "}
                <ButtonLink to="/app/account" variant="link" size="sm" className="px-0">
                  fix it in your account settings
                </ButtonLink>
                .
              </div>
            )}
            {cocore?.out_of_credits && (
              <div className="text-sm text-slate-dim">
                Paused: you are out of co/core credits
                {cocore.balance !== null
                  ? ` (${cocore.balance.toLocaleString()} left; co/core needs ${cocore.min_credits.toLocaleString()} to run a job)`
                  : ""}
                . Pulses resume on their own once your balance is back above the floor;{" "}
                <a
                  className="underline"
                  href={cocore.topup_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  see your co/core dashboard
                </a>
                .
              </div>
            )}
            {feed.settings?.feed_pulse_enabled && (
              <div className="text-xs text-slate-dim">
                {inFlight
                  ? "Writing yesterday's pulse now; this usually takes one to three minutes. "
                  : latest?.pulse_date
                    ? `Last pulse: ${latest.pulse_date}${
                        latest.provider?.handle
                          ? ` on @${latest.provider.handle}'s ${latest.provider.machineLabel ?? "Mac"}`
                          : ""
                      }. `
                    : "No pulse yet. "}
                {!inFlight && lastJob && lastJob.state !== "done" && (
                  <>
                    Last attempt: {lastJob.state}
                    {lastJob.error_code ? ` (${lastJob.error_code})` : ""}.{" "}
                  </>
                )}
                {!inFlight && (
                  <Button
                    variant="link"
                    size="sm"
                    className="px-0 h-auto text-xs"
                    disabled={pulseNowMutation.isPending}
                    onClick={() => pulseNowMutation.mutate()}
                  >
                    {latest?.pulse_date ? "Write yesterday's pulse again" : "Write yesterday's pulse now"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        <Switch
          aria-label="Daily Feed Pulse"
          defaultChecked={feed.settings?.feed_pulse_enabled ?? false}
          disabled={!connected}
          onCheckedChange={onToggle}
          id="feed-pulse-enabled"
        />
      </div>
    </div>
  );
}

function RouteComponent() {
  const { feedId } = Route.useParams();
  const { data: feed } = useSuspenseQuery(feedQueryOptions(parseInt(feedId)));
  // useQuery, not useSuspenseQuery: if this request fails the settings page must still render.
  // Undefined then reads as "not available", so a backend that cannot answer hides the opt-in
  // rather than offering one whose outcome it cannot honour.
  const isSimpleEditor = isSimpleEditorSnapshot(
    feed.draft_algorithm_manifest.metadata?.snapshot,
  );
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const { toast } = useToast();
  const clearFeedMutation = useMutation({
    mutationFn: () => clearFeed(feed.id),
    onSuccess: () =>
      toast({
        title: "Feed clearing",
        description:
          "Your feed is being cleared. It may take a few minutes to complete.",
      }),
    onError: (error) =>
      toast({
        title: "Error clearing feed",
        description: error.message,
        variant: "destructive",
      }),
  });
  const backfillStatusQuery = useQuery({
    queryKey: ["backfill-status", feed.id],
    queryFn: () => getBackfillStatus(feed.id),
    refetchInterval: (query) => (query.state.data?.in_progress ? 5000 : false),
  });
  // Server-decided, not re-derived here: a raw `status` of "queued" can outlive
  // its job by up to the 7-day record TTL, and testing for it directly used to
  // leave these buttons disabled for days on feeds the API would happily
  // backfill. `in_progress` is the same predicate the routes themselves gate
  // on, so the button and the endpoint behind it can no longer disagree.
  const backfillActive = backfillStatusQuery.data?.in_progress === true;
  // The feed's own backfill button, used by whoever is allowed to run one. A collaborator
  // holding `trigger_backfill` presses this, not a second button somewhere else; only the
  // endpoint underneath differs. See `lib/feedWrites.ts`.
  const feedAccess = useFeedAccess(feed);
  const backfillFeedMutation = useMutation({
    mutationFn: () =>
      runBackfill({ feedId: feed.id, asCollaborator: feedAccess.isCollaborator }),
    onSuccess: (status) =>
      toast({
        title: status.started
          ? "Feed backfill started..."
          : "Backfill not started",
        description:
          status.note ??
          "Your feed is being backfilled. It may take a few minutes to complete.",
      }),
    onError: (error) =>
      toast({
        title: "Couldn't start backfill",
        description:
          (isAxiosError<{ detail?: string }>(error) &&
            error.response?.data?.detail) ||
          error.message,
        variant: "destructive",
      }),
    onSettled: () => backfillStatusQuery.refetch(),
  });
  const rebuildFeedMutation = useMutation({
    mutationFn: () => rebuildFeed(feed.id),
    onSuccess: (status) =>
      toast({
        title: "Feed rebuild started...",
        description:
          status.warning ??
          "Your feed was cleared and is being refilled with the last 24 hours of matching posts.",
      }),
    onError: (error) =>
      toast({
        title: "Couldn't rebuild feed",
        description:
          (isAxiosError<{ detail?: string }>(error) &&
            error.response?.data?.detail) ||
          error.message,
        variant: "destructive",
      }),
    onSettled: () => backfillStatusQuery.refetch(),
  });
  const setPublicMutation = useMutation({
    mutationFn: (isPublic: boolean) => setFeedPublic(feed.id, isPublic),
    onSuccess: () =>
      toast({
        title: "Feed visibility updated",
        description: "Your feed visibility has been updated.",
      }),
    onError: (error) =>
      toast({
        title: "Error updating feed visibility",
        description: error.message,
        variant: "destructive",
      }),
  });
  const setContrailsMutation = useMutation({
    mutationFn: (isContrails: boolean) =>
      setContrailsForAlgorithm({
        algo_id: feed.id,
        contrails: isContrails,
      }),
    onSuccess: () =>
      toast({
        title: "Contrails updated",
        description: "Your feed's contrails settings have been updated",
      }),
    onError: (error) =>
      toast({
        title: "Error updating feed contrails settings",
        description: error.message,
        variant: "destructive",
      }),
  });
  const removeSubscriberListMutation = useMutation({
    mutationFn: (listId: number) =>
      removeSubscriberListFromFeed(feed.id, listId),
    onSuccess: () =>
      toast({
        title: "Feed subscriber list removed",
        description: "Your feed subscriber list has been removed.",
      }),
    onError: (error) =>
      toast({
        title: "Error removing feed subscriber list",
        description: error.message,
        variant: "destructive",
      }),
  });
  const setFeedSettingMutation = useMutation({
    mutationFn: (options: AppSetting) => {
      return setAlgoSetting(feed.id, options);
    },
    onError: (error) =>
      toast({
        title: "Error updating settings",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <Page>
      <ViewFeedHeader
        feedId={feedId}
        icon={
          <div className="h-full w-full flex items-center justify-center">
            <ButtonLink
              to="/app/feed/$feedId/view"
              params={{ feedId }}
              variant="ghost"
              size="icon"
            >
              <span className="sr-only">Back to feed</span>
              <ArrowLeftIcon />
            </ButtonLink>
          </div>
        }
      />
      <div className="flex flex-col gap-10">
        {/*
          Self-improvement USED TO BE HERE and is now in the editor, on the feed node beside the
          ordering and the audiences -- the only things it varies. It is not duplicated here on
          purpose: two switches over one `feed_gym_enabled` would disagree the moment one of them
          held a stale `defaultChecked`, which is exactly what this one did.
        */}
        {/* GA since 2026-09-17: no longer flag-gated. Kill switches live in
            feed_pulse_config, not in a per-user flag.

            🔑 OWNER ONLY, AND NOT BEHIND A CAPABILITY. A pulse is written against the
            OWNER's co/core connection and spends the OWNER's credits, and both endpoints
            behind this section are guarded server-side by `authorize_object` -- plain
            ownership. Shown to a collaborator it offers a co/core link that would bind
            THEIR account and a toggle that can only 403. Same reasoning as Patreon. */}
        {feedAccess.isOwner && (
          <FeedPulseSettings
            feed={feed}
            onToggle={(enabled) =>
              setFeedSettingMutation.mutate(
                {
                  setting_name: "feed_pulse_enabled",
                  setting_value: enabled,
                },
                {
                  // Turning it on writes yesterday's pulse right away, so the
                  // owner sees the card in a minute or two rather than after
                  // the next 06:20 UTC run. Best effort: the daily cron still
                  // covers the feed if this call fails.
                  onSuccess: () => {
                    if (enabled) {
                      requestFeedPulseNow(feed.id).catch(() => undefined);
                    }
                  },
                },
              )
            }
          />
        )}

        <div className="flex flex-col gap-4">
          <H2 className="mt-2">Editor Settings</H2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[60ch]">
                <div className="md:text-lg font-medium">Show NSFW</div>
                <div className="text-sm text-slate-dim">
                  By default, NSFW content will be blurred within the Graze
                  editor. Use this option to disable that for this feed only.
                </div>
              </div>
            </div>
            <Switch
              aria-label="Show NSFW"
              defaultChecked={
                feed.settings?.show_nsfw ?? user.settings?.show_nsfw ?? false
              }
              onCheckedChange={(showNsfw) =>
                setFeedSettingMutation.mutate({
                  setting_name: "show_nsfw",
                  setting_value: showNsfw,
                })
              }
              id="public-mode"
            />
          </div>
          {isSimpleEditor ? (
            <SwitchToFullEditorSetting feedId={feed.id} />
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex flex-col gap-1 max-w-[60ch]">
                  <div className="md:text-lg font-medium">Editor Mode</div>
                  <div className="text-sm text-slate-dim">
                    Set the default editor mode for this feed.
                  </div>
                </div>
              </div>
              <SimpleSelect
                options={[
                  { label: "JSON", value: "json" },
                  { label: "Canvas", value: "canvas" },
                ]}
                aria-label="Editor Mode"
                onValueChange={(value) =>
                  setFeedSettingMutation.mutate({
                    setting_name: "editor_mode",
                    setting_value: value,
                  })
                }
                value={feed.settings?.editor_mode || "canvas"}
              />
            </div>
          )}
        </div>

        {/* 🔑 OWNER ONLY, ENTIRE SECTION. Who may READ the feed, who is banned, what sits
            behind a paywall, and the Patreon link itself. None of it is covered by a
            collaborator capability, and the Patreon button is the sharpest edge on the
            page: it starts an OAuth flow that binds THE CLICKER'S OWN Patreon account, so
            a collaborator pressing it would attach their account, not the owner's. */}
        {feedAccess.isOwner && (
        <div className="flex flex-col gap-4 mt-4">
          <H2 className="mt-2">Access Control</H2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[40ch]">
                <div className="md:text-lg font-medium">Subscriber Lists</div>
                <div className="text-sm text-slate-dim">
                  Restrict feed access to lists of users.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {feed.subscriber_lists.map((l) => (
                <div
                  key={`list-${l.id}`}
                  className={cn(
                    "rounded-md flex items-center",
                    l.list_type === "memberships"
                      ? "bg-green-4 dark:bg-greendark-4 text-green-normal"
                      : "bg-red-4 dark:bg-reddark-4 text-red-normal",
                  )}
                >
                  <span className="px-2 py-1">{l.display_name}</span>
                  <Button
                    variant="ghost"
                    size="smIcon"
                    onPress={() => removeSubscriberListMutation.mutate(l.id)}
                  >
                    <Cross1Icon />
                    <span className="sr-only">Remove list</span>
                  </Button>
                </div>
              ))}

              <AddSubscriberListButton feed={feed} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[40ch]">
                <div className="md:text-lg font-medium">Banned Post</div>
                <div className="text-sm text-slate-dim">
                  A post or list of posts that will be shown to users who have
                  been banned from the feed.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {Boolean(
                feed.settings?.banned_post?.length &&
                  feed.settings.banned_post.length > 0,
              ) && (
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge asChild noIcon>
                        <button type="button">{feed.settings?.banned_post?.length}</button>
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-[min(60ch,90vw)]">
                      <div className="flex flex-col gap-2">
                        {feed.settings?.banned_post?.map((p) => (
                          <div key={p.uri}>
                            <PreviewPost
                              record={p}
                              actions={
                                <Button
                                  variant="ghost"
                                  size="smIcon"
                                  onPress={() =>
                                    setFeedSettingMutation.mutate({
                                      setting_name: "banned_post",
                                      setting_value:
                                        feed.settings?.banned_post
                                          ?.filter(
                                            (banndedPost) =>
                                              banndedPost.uri !== p.uri,
                                          )
                                          .map((p) => p.uri) ?? [],
                                    })
                                  }
                                >
                                  <Cross1Icon />
                                  <span className="sr-only">Remove post</span>
                                </Button>
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              <PostListDialog
                title="Add Banned Post"
                description="Add a post or list of posts that will be shown to users who have been banned from the feed."
                onAdd={(posts) => {
                  setFeedSettingMutation.mutate({
                    setting_name: "banned_post",
                    setting_value: [
                      ...(feed.settings?.banned_post ?? []),
                      ...posts,
                    ].map((p) => atUriToBskyUrl(p.uri)),
                  });
                }}
              >
                <Button size="smIcon" variant="ghost">
                  <span className="sr-only">Edit banned post</span>
                  <Edit2Icon />
                </Button>
              </PostListDialog>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[40ch]">
                <div className="flex items-center gap-2">
                  <div className="md:text-lg font-medium">
                    Members Only Post
                  </div>
                  <PatreonWarning feedId={feed.id} />
                </div>
                <div className="text-sm text-slate-dim">
                  A post or list of posts that will be shown to users who need
                  membership access to the feed.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {Boolean(
                feed.settings?.members_only_post?.length &&
                  feed.settings.members_only_post.length > 0,
              ) && (
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge asChild noIcon>
                        <button type="button">{feed.settings?.members_only_post?.length}</button>
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-[min(60ch,90vw)]">
                      <div className="flex flex-col gap-2">
                        {feed.settings?.members_only_post?.map((p) => (
                          <div key={p.uri}>
                            <PreviewPost
                              record={p}
                              actions={
                                <Button
                                  variant="ghost"
                                  size="smIcon"
                                  onPress={() =>
                                    setFeedSettingMutation.mutate({
                                      setting_name: "members_only_post",
                                      setting_value:
                                        feed.settings?.members_only_post
                                          ?.filter(
                                            (memberPost) =>
                                              memberPost.uri !== p.uri,
                                          )
                                          .map((p) => p.uri) ?? [],
                                    })
                                  }
                                >
                                  <Cross1Icon />
                                  <span className="sr-only">Remove post</span>
                                </Button>
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              <PostListDialog
                title="Add Members Only Post"
                description="A post or list of posts that will be shown to users who need membership access to the feed."
                onAdd={(posts) => {
                  setFeedSettingMutation.mutate({
                    setting_name: "members_only_post",
                    setting_value: [
                      ...(feed.settings?.members_only_post ?? []),
                      ...posts,
                    ].map((p) => atUriToBskyUrl(p.uri)),
                  });
                }}
              >
                <Button size="smIcon" variant="ghost">
                  <span className="sr-only">Edit members only post</span>
                  <Edit2Icon />
                </Button>
              </PostListDialog>
            </div>
          </div>
          <PatreonSettings feedId={feed.id} />
        </div>
        )}

        <div className="flex flex-col gap-4">
          <H2 className="mt-2">Feed Settings</H2>
          {/* Visibility, public egress and the destructive buttons are the owner's. The
              backfill below is NOT: `trigger_backfill` is a real capability, and it stays
              where a collaborator would look for it. */}
          {feedAccess.isOwner && (
          <>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[60ch]">
                <div className="md:text-lg font-medium">Public</div>
                <div className="text-sm text-slate-dim">
                  Set whether the feed definition is public. This enables you to
                  share how your custom algorithm works and lets people remix
                  it.
                </div>
              </div>
            </div>
            <Switch
              aria-label="Public"
              defaultChecked={feed.public}
              onCheckedChange={(isPublic) => setPublicMutation.mutate(isPublic)}
              id="public-mode"
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[60ch]">
                <div className="md:text-lg font-medium">Contrails</div>
                <div className="text-sm text-slate-dim">
                  Set whether or not your feed produces contrails, a websocket
                  endpoint for streaming output for live programmatic access.{" "}
                  <Link
                    to="/docs/$slug"
                    params={{ slug: "contrails" }}
                    className="inline-flex items-center gap-1"
                    style={{ color: "inherit" }}
                  >
                    Learn more
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
            <Switch
              aria-label="Contrails"
              defaultChecked={feed.contrails}
              onCheckedChange={(isContrails) =>
                setContrailsMutation.mutate(isContrails)
              }
              id="contrails-mode"
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[40ch]">
                <div className="md:text-lg font-medium">Clear feed history</div>
                <div className="text-sm text-slate-dim">
                  Delete all posts that are currently stored for the feed. This
                  is a destructive action and cannot be undone.
                </div>
              </div>
            </div>
            <Button
              variant="destructive"
              isLoading={clearFeedMutation.isPending}
              onPress={() => clearFeedMutation.mutate()}
            >
              Clear Feed
            </Button>
          </div>
          </>
          )}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[40ch]">
                <div className="md:text-lg font-medium">Backfill feed</div>
                <div className="text-sm text-slate-dim">
                  Backfill the feed with the last 6 hours of new matching posts.
                  If you want old posts cleared out of your feed first clear the
                  feed.
                  {backfillActive && (
                    <span className="block mt-1 font-medium">
                      A backfill is currently{" "}
                      {backfillStatusQuery.data?.status === "running"
                        ? "running"
                        : "queued"}
                      …
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="destructive"
              disabled={backfillActive || !feedAccess.can("trigger_backfill")}
              isLoading={backfillFeedMutation.isPending}
              onPress={() => backfillFeedMutation.mutate()}
            >
              {backfillActive ? "Backfill in progress" : "Backfill Feed"}
            </Button>
          </div>
          {feedAccess.isOwner && (
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex flex-col gap-1 max-w-[40ch]">
                <div className="md:text-lg font-medium">Rebuild feed</div>
                <div className="text-sm text-slate-dim">
                  Clear all stored posts, then refill the feed with the last 24
                  hours of posts matching the current definition. Use this
                  after changing what your feed matches. Cannot be undone.
                </div>
              </div>
            </div>
            <Button
              variant="destructive"
              disabled={backfillActive}
              isLoading={rebuildFeedMutation.isPending}
              onPress={() => rebuildFeedMutation.mutate()}
            >
              {backfillActive ? "Backfill in progress" : "Rebuild Feed"}
            </Button>
          </div>
          )}
        </div>

        {/* Money is the owner's, flatly: CPM, saturation, programmatic partners and the
            payout onboarding behind "Set up monetization" all settle against THEIR
            account. */}
        {feedAccess.isOwner && (
          <MonetizationSection feed={feed} key={feed.cpm_cents} />
        )}
        {!feedAccess.isOwner && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Visibility, access, monetization, Feed Pulse and this feed&apos;s Patreon
            belong to its owner, and are not shown here.
          </p>
        )}
      </div>
    </Page>
  );
}
